import { useState } from 'react';
import { TableType } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import type { TableMobileView, TableRow } from '../../template/Table';
import { useTableEditor } from './useTableEditor';
import { useTableImport } from './useTableImport';
import { useTableMobileView } from './useTableMobileView';

/**
 * State + handlers for `TableBlockEditor`: the table editor itself, pending-rows
 * replace-confirmation, the desktop/mobile view tab, the import pipeline, and the
 * mobile-view generator — all keyed off the same `onChange`-normalising path.
 */
export function useTableBlockEditorState(
  block: TableType,
  onChange: (props: TableType['properties']) => void,
  modelId: string,
  backendService?: BackendService,
  modelType: string = 'FundOpportunity',
) {
  const { properties } = block;
  const editor = useTableEditor(properties, onChange);
  const [pendingRows, setPendingRows] = useState<TableRow[] | null>(null);
  const [viewTab, setViewTab] = useState<'desktop' | 'mobile'>('desktop');

  const applyRows = (rows: TableRow[]) => {
    if (properties.rows.length > 0) setPendingRows(rows);
    else onChange({ ...properties, rows });
  };
  const importer = useTableImport(backendService, modelId, applyRows, modelType);

  const applyMobileView = (view: TableMobileView | null) => {
    if (view) {
      onChange({ ...properties, mobileView: view });
    } else {
      const { mobileView: _dropped, ...rest } = properties;
      onChange(rest);
    }
  };
  const mobileView = useTableMobileView(
    backendService,
    properties.rows,
    properties.mobileView,
    applyMobileView,
  );

  return {
    properties,
    editor,
    pendingRows,
    setPendingRows,
    viewTab,
    setViewTab,
    importer,
    mobileView,
  };
}
